import { useCallback, useEffect, useState } from "react";
import type { RefObject } from "react";

import { clampZoom } from "@/lib/zoom";

interface FitToWidthZoomOptions {
  /** Scroll container whose client width bounds the page. */
  scrollRef: RefObject<HTMLElement | null>;
  /** Natural (unscaled) page width, measured by the viewer after render. */
  naturalWidthRef: RefObject<number>;
  /** Chrome (px) reserved around the page when fitting to width. */
  stageInset: number;
  /** When true, re-fit on pane resize while fit-to-width mode is active. */
  refitOnResize: boolean;
}

/**
 * Shared fit-to-width zoom state for document viewers: manual −/+ zoom, a fit
 * mode that tracks the pane width, and a ResizeObserver that re-fits while fit
 * mode is active.
 */
export function useFitToWidthZoom({ scrollRef, naturalWidthRef, stageInset, refitOnResize }: FitToWidthZoomOptions) {
  const [zoom, setZoom] = useState(1);
  const [fitMode, setFitMode] = useState(true);

  const computeFitZoom = useCallback(() => {
    const scroll = scrollRef.current;
    const pageWidth = naturalWidthRef.current;
    if (!scroll || pageWidth <= 0) {
      return null;
    }
    const available = scroll.clientWidth - stageInset;
    if (available <= 0) {
      return null;
    }
    return clampZoom(available / pageWidth);
  }, [naturalWidthRef, scrollRef, stageInset]);

  const fitToWidth = useCallback(() => {
    const next = computeFitZoom();
    setFitMode(true);
    if (next != null) {
      setZoom(next);
    }
  }, [computeFitZoom]);

  const setManualZoom = useCallback((next: number) => {
    setFitMode(false);
    setZoom(clampZoom(next));
  }, []);

  /** Adjust zoom (clamped) without leaving fit mode. */
  const setFitZoom = useCallback((next: number) => {
    setZoom(clampZoom(next));
  }, []);

  /** Reset to fit mode at natural scale (used before a fresh render pass). */
  const resetZoom = useCallback(() => {
    setFitMode(true);
    setZoom(1);
  }, []);

  /** Enter fit mode at the computed fit zoom (falling back to natural scale). */
  const applyFitZoom = useCallback(() => {
    setFitMode(true);
    setZoom(computeFitZoom() ?? 1);
  }, [computeFitZoom]);

  useEffect(() => {
    const scroll = scrollRef.current;
    if (!refitOnResize || !scroll || !fitMode) {
      return;
    }
    const observer = new ResizeObserver(() => {
      const next = computeFitZoom();
      if (next != null) {
        setZoom(next);
      }
    });
    observer.observe(scroll);
    return () => observer.disconnect();
  }, [computeFitZoom, fitMode, refitOnResize, scrollRef]);

  return { zoom, setFitZoom, fitMode, computeFitZoom, fitToWidth, setManualZoom, resetZoom, applyFitZoom };
}
